import React from 'react';
import CustomMedia from 'util/custom-media-query';
import FormDialogrenderer from './FormDialogRenderer';
import { Field } from 'react-final-form';
import i18n from 'i18n';
import { css } from 'styled-components';
import { applyStyles, convertSpace } from 'util/helpers';
import Button from 'components/common/Button';
import FontStyles from 'constants/font-styles';
import Colors from 'constants/colors';
import {
  composeValidators,
  breakMaxLength,
  surrogatePairPattern,
  maxLengthWithLine,
  maxLengthRemovedNewline,
  required
} from 'validation/validators';
import FormError from 'components/common/FormError';
import SelectField from 'components/common/SelectField';
import MessageCardType from 'constants/message-card-type';
import * as validationMessages from 'validation/i18n/ja';
import MessageCardPreviewDialog from './MessageCardPreviewDialog';

class DialogBody extends React.Component {
  constructor(props) {
    super(props);

    // エラーメッセージの出し方を独自で制御するため final-form の touched を使用しない。
    this.state = {
      touched: false
    };
  }

  handleChangeTouched = () => {
    if (!this.state.touched) {
      this.setState({
        touched: true
      });
    }
  };

  handleChangeInput = event => {
    const position = event.nativeEvent.target.selectionEnd;
    const replaced = convertSpace(event.nativeEvent.target.value);
    const {
      form: { change }
    } = this.props;

    change('messageCard', replaced);
    event.nativeEvent.target.value = replaced;
    event.nativeEvent.target.selectionEnd = position;
  };

  handlePreview = async () => {
    await MessageCardPreviewDialog({ message: this.props.values.messageCard });
  };

  render() {
    const {
      values: { messageCardType },
      hasValidationErrors
    } = this.props;
    return (
      <div className="message-card">
        <SelectField
          name="messageCardType"
          className="message-card-type"
          options={[
            {
              label: i18n.t(
                'orderAggregators:common:order.messageCardTypeNone'
              ),
              value: MessageCardType.none
            },
            {
              label: i18n.t(
                'orderAggregators:common:order.messageCardTypeInput'
              ),
              value: MessageCardType.input
            },
            {
              label: i18n.t(
                'orderAggregators:common:order.messageCardTypeWhitePaper'
              ),
              value: MessageCardType.whitePaper
            }
          ]}
        />
        {messageCardType === MessageCardType.none && (
          <Field
            name="messageCard"
            render={({ input }) => (
              <React.Fragment>
                <textarea disabled={true} {...input} />
                <span className="current-length">{`${
                  input.value.length
                } / 200 ${i18n.t('common:characters')}( ${
                  input.value.split('\n').length
                } / 10 ${i18n.t('common:lines')})`}</span>
              </React.Fragment>
            )}
          />
        )}
        {messageCardType === MessageCardType.whitePaper && (
          <Field
            name="messageCard"
            render={({ input }) => (
              <React.Fragment>
                <textarea disabled={true} {...input} />
                <span className="current-length">{`${
                  input.value.length
                } / 200 ${i18n.t('common:characters')}( ${
                  input.value.split('\n').length
                } / 10 ${i18n.t('common:lines')})`}</span>
              </React.Fragment>
            )}
          />
        )}
        {messageCardType === MessageCardType.input && (
          <Field
            name="messageCard"
            validate={value =>
              composeValidators(
                required,
                surrogatePairPattern,
                breakMaxLength,
                maxLengthWithLine(20),
                maxLengthRemovedNewline(200)
              )(value)
            }
            parse={value => (value ? value : '')}
            render={({ input, meta }) => (
              <React.Fragment>
                <textarea
                  className={`${
                    meta.error && this.state.touched ? 'has-error' : ''
                  }`}
                  {...input}
                  onBlur={() => this.handleChangeTouched()}
                  onChange={event => {
                    this.handleChangeTouched();
                    input.onChange(event.nativeEvent.value);
                    this.handleChangeInput(event);
                  }}
                />
                <span className="current-length">
                  <span
                    className={`current ${
                      meta.error &&
                      meta.error.includes(validationMessages.maxLength(200))
                        ? 'has-error'
                        : ''
                    }`}
                  >
                    {input.value.replace(/\n/g, '').length}
                  </span>
                  {` / 200 ${i18n.t('common:characters')}`}&thinsp;{`( `}
                  <span
                    className={`current ${
                      meta.error &&
                      meta.error.includes(
                        i18n.t('common:validationMessages.breakMaxLength')
                      )
                        ? 'has-error'
                        : ''
                    }`}
                  >
                    {input.value.split('\n').length}
                  </span>
                  {` / 10 ${i18n.t('common:lines')})`}
                </span>
                <div className="form-error">
                  <FormError {...meta} touched={this.state.touched} />
                </div>
              </React.Fragment>
            )}
          />
        )}
        <div className="preview">
          <Button
            type="button"
            buttonType="H"
            disabled={
              hasValidationErrors || messageCardType !== MessageCardType.input
            }
            onClick={() => this.handlePreview()}
          >
            {i18n.t('common:preview')}
          </Button>
        </div>
      </div>
    );
  }
}

const DialogFooter = ({ handleSubmit, hasValidationErrors }) => (
  <div className="footer-btn-wrapper">
    <Button
      buttonType="B"
      type="button"
      onClick={handleSubmit}
      disabled={hasValidationErrors}
    >
      {i18n.t('common:save')}
    </Button>
  </div>
);

const MessageCardDialog = ({
  className,
  onSubmit,
  dialogRef,
  initialValues
}) => (
  <div className={className}>
    <FormDialogrenderer
      title={i18n.t('orderAggregators:common:order.messageCardDialogTitle')}
      innerRef={dialogRef}
      renderBody={props => <DialogBody {...props} />}
      renderFooter={props => <DialogFooter {...props} />}
      initialValues={initialValues}
      onSubmit={onSubmit}
      showCloseAlert={true}
    />
  </div>
);

const commonStyle = css`
  .footer-btn-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }

  .notification {
    white-space: pre-wrap;
  }

  textarea {
    outline: none;
    resize: none;
    &.has-error {
      background-color: ${Colors.orange2};
      border: 4px solid ${Colors.orange1};
    }

    &:disabled {
      opacity: 0.3;
      background-color: ${Colors.gray4};
    }
  }

  .form-error {
    margin-top: 2px;
    > * {
      color: ${Colors.orange1};
    }
  }

  .current {
    display: inline;
  }

  .preview {
    border-top: 1px dashed ${Colors.gray2};
    display: flex;
    justify-content: center;
    align-items: center;

    ${Button}:disabled {
      cursor: not-allowed;
    }
  }
`;

const pcStyle = CustomMedia.greaterThan('umedium')`
  .message-card {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 32px 32px 0;
  }

  span,
  textarea {
    color: ${Colors.black1};
  }

  .current-length {
    ${FontStyles.text10};
    display: block;
    text-align: right;

    margin: 4px 0 0;

    .has-error {
      ${FontStyles.text9};
      color: ${Colors.orange1};
    }
  }

  .form-error {
    span {
      margin: 0;
      display: block;
      text-align: right;
    }
  }

  .notification {
    ${FontStyles.text12};

    margin: 0;
  }

  .message-card-type {
    margin: 0;

    select {
      width: 270px;
    }
  }

  textarea {
    ${FontStyles.text8};

    width: 100%;
    height: 448px;
    padding: 12px;
    margin: 32px 0 0;
    border: 1px solid ${Colors.gray2};
    border-radius: 4px;
  }

  span {
    flex-shrink: 0;
  }

  .preview {
    margin-top: 24px;
    min-height: 80px;
  }

`;

const spStyle = CustomMedia.lessThan('medium')`
  .message-card {
    display: flex;
    flex-direction: column;
    padding: 16px 0 0;
  }

  span,
  textarea {
    color: ${Colors.black1};
  }

  span {
    display: block;
    text-align: right;
  }

  .current-length {
    ${FontStyles.text6};

    margin: 2px 16px 0;

    .has-error {
      ${FontStyles.text5};
      color: ${Colors.orange1};
    }
  }

  .form-error {
    margin: 4px 16px 0;
    span {
      ${FontStyles.text7};
      margin: 0;
    }
  }

  .notification {
    ${FontStyles.text8};

    margin: 0 16px;
  }

  .message-card-type {
    margin: 0;

    select {
      width: 100%;
    }
  }

  textarea {
    ${FontStyles.text6};

    width: calc(100% - 32px);
    height: 300px;
    padding: 10px;
    margin: 16px 16px 0;
    border: 1px solid ${Colors.gray2};
    border-radius: 4px;
  }

  span {
    flex-shrink: 0;
  }

  .preview {
    margin-top: 12px;
    min-height: 72px;
  }

`;

export default applyStyles(MessageCardDialog)(commonStyle, pcStyle, spStyle);



// WEBPACK FOOTER //
// ./src/components/OrderAggregators/components/MessageCardDialog.js